import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../AppContext";
import useScope from "./useScope";
import useDismiss from "./useDismiss";
import { getInitials } from "./routeMeta";

const byName = (a, b) => (a.name || "").localeCompare(b.name || "");

const ScopeItem = ({ name, avatar, avatarClass, active, onClick, hint }) => (
  <li>
    <button type="button" className={`scope-item ${active ? "active" : ""}`} onClick={onClick}>
      <span className={`scope-avatar ${avatarClass}`}>{avatar}</span>
      <span className="scope-item-text">
        <span className="scope-item-name">{name}</span>
        {hint && <span className="scope-item-hint">{hint}</span>}
      </span>
      {active && <i className="icon-check scope-item-check" />}
    </button>
  </li>
);

const ScopeSwitcher = ({ collapsed }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, currentOrg, currentProject, setMobileNavOpen } = useAppContext();
  const { organizations, projects, orgName, projectName, selectOrg, selectProject } = useScope();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const cardRef = useRef(null);
  const popRef = useRef(null);
  const dismissRefs = useMemo(() => [cardRef, popRef], []);

  const canSwitchOrg = currentUser.role === "superadmin";
  const canSwitchDomain = currentUser.role !== "user";
  const isInteractive = canSwitchOrg || canSwitchDomain;

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
  }, []);

  useDismiss(isOpen, dismissRefs, close);

  useLayoutEffect(() => {
    if (!isOpen || !cardRef.current) return undefined;
    const place = () => {
      const rect = cardRef.current.getBoundingClientRect();
      const nav = cardRef.current.closest("nav");
      const navRight = nav ? nav.getBoundingClientRect().right : rect.right;
      setPosition({ top: rect.top, left: navRight + 12 });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [isOpen, collapsed]);

  const q = query.trim().toLowerCase();
  const matches = (name) => !q || (name || "").toLowerCase().includes(q);
  const visibleOrgs = organizations.filter((org) => matches(org.name)).sort(byName);
  const visibleProjects = projects.filter((project) => matches(project.name)).sort(byName);
  const showAllDomains = projects.length > 1 && matches(t("all_domains"));

  const pickProject = (id, name) => {
    selectProject(id, name);
    close();
    setMobileNavOpen(false);
  };

  const goTo = (path) => {
    close();
    setMobileNavOpen(false);
    navigate(path);
  };

  const onSearchKeyDown = (event) => {
    if (event.key !== "Enter") return;
    if (visibleProjects.length > 0) pickProject(visibleProjects[0].id, visibleProjects[0].name);
    else if (canSwitchOrg && visibleOrgs.length === 1) selectOrg(visibleOrgs[0]);
  };

  return (
    <>
      <button
        type="button"
        ref={cardRef}
        className={`scope-card ${isInteractive ? "" : "static"} ${isOpen ? "open" : ""}`}
        onClick={() => isInteractive && setIsOpen(!isOpen)}
        title={collapsed ? `${orgName} · ${projectName}` : undefined}
        aria-haspopup={isInteractive ? "dialog" : undefined}
        aria-expanded={isInteractive ? isOpen : undefined}
      >
        <span className="scope-avatar org">{getInitials(orgName)}</span>
        <span className="scope-card-text">
          <span className="scope-card-org">{orgName}</span>
          <span className="scope-card-domain">{projectName}</span>
        </span>
        {isInteractive && <i className="icon-sort scope-card-chevron" />}
      </button>

      {isOpen && createPortal(
        <div
          ref={popRef}
          className={`scope-pop ${canSwitchOrg ? "two-cols" : ""}`}
          style={{ top: position.top, left: position.left }}
          role="dialog"
          aria-label={t("shell.switch_scope")}
        >
          <div className="scope-pop-search">
            <i className="icon-search" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder={canSwitchOrg ? t("shell.search_org_or_domain") : t("shell.search_domain")}
            />
            <kbd>esc</kbd>
          </div>

          <div className="scope-pop-cols">
            {canSwitchOrg && (
              <div className="scope-col">
                <div className="scope-col-title">
                  {t("organizations")}
                  <span className="scope-col-count">{organizations.length}</span>
                </div>
                <ul>
                  {visibleOrgs.map((org) => (
                    <ScopeItem
                      key={org.id}
                      name={org.name}
                      hint={org.securityStandard || "ISO27001"}
                      avatar={getInitials(org.name)}
                      avatarClass="org"
                      active={org.id === currentOrg.id}
                      onClick={() => selectOrg(org)}
                    />
                  ))}
                  {visibleOrgs.length === 0 && <li className="scope-empty">{t("no_results")}</li>}
                </ul>
              </div>
            )}

            <div className="scope-col">
              <div className="scope-col-title">
                {t("shell.domains_in", { org: orgName })}
                <span className="scope-col-count">{projects.length}</span>
              </div>
              <ul>
                {showAllDomains && (
                  <ScopeItem
                    name={t("all_domains")}
                    hint={t("shell.all_domains_hint")}
                    avatar={<i className="icon-overview" />}
                    avatarClass="all"
                    active={!currentProject.id}
                    onClick={() => pickProject("", t("all_domains"))}
                  />
                )}
                {visibleProjects.map((project) => (
                  <ScopeItem
                    key={project.id}
                    name={project.name}
                    avatar={getInitials(project.name)}
                    avatarClass="domain"
                    active={project.id === currentProject.id}
                    onClick={() => pickProject(project.id, project.name)}
                  />
                ))}
                {!showAllDomains && visibleProjects.length === 0 && <li className="scope-empty">{t("no_results")}</li>}
              </ul>
            </div>
          </div>

          <div className="scope-pop-footer">
            <button type="button" onClick={() => goTo("/projects")}>
              <i className="icon-Projecten" /> {t("shell.manage_domains")}
            </button>
            {canSwitchOrg && (
              <button type="button" onClick={() => goTo("/organizations")}>
                <i className="icon-Organisaties" /> {t("shell.manage_organizations")}
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ScopeSwitcher;
